import { memo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  cn,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@scio3/ui';
import {
  Minus,
  ChevronsRight,
  MessageCirclePlus,
  PanelRight,
  PictureInPicture2,
  MessageSquare,
  History,
  Check,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';
import { formatWithPattern } from '@/lib/format-date';
import { useOptibotStore, type OptibotViewMode } from '../stores/optibot-store';
import { getChatApiUrl } from '../utils/constants';
import { BotAvatar } from './BotAvatar';

/** Icon shown on the view-mode trigger, per active mode. */
const VIEW_ICONS: Record<OptibotViewMode, LucideIcon> = {
  sidebar: PanelRight,
  floating: PictureInPicture2,
};

/** Selectable view-mode options, in menu order. */
const VIEW_OPTIONS: ReadonlyArray<{
  value: OptibotViewMode;
  icon: LucideIcon;
  labelKey: string;
}> = [
  { value: 'sidebar', icon: PanelRight, labelKey: 'optibot.view.sidebar' },
  { value: 'floating', icon: PictureInPicture2, labelKey: 'optibot.view.floating' },
];

interface ConversationThread {
  threadId: string;
  subject: string;
  lastMessagePreview: string;
  createdAt: string;
  updatedAt: string;
}

interface OptiBotHeaderProps {
  onNewChat?: () => void;
  onLoadConversation?: (threadId: string) => void;
  activeThreadId?: string | null;
  hasMessages?: boolean;
  /** When the message list is scrolled down, show the header's bottom scroll shadow. */
  isScrolled?: boolean;
}

/** Ghost icon button with a tooltip — used for all plain icon actions in the header. */
function TipIconButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onClick}
          aria-label={label}
          className="cursor-pointer"
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Panel header — OptiBot avatar + title, history dropdown, new chat button,
 * view-mode switcher, and the minimize button.
 * Wrapped in React.memo to prevent re-renders when only the input state changes in the parent.
 */
export const OptiBotHeader = memo(function OptiBotHeader({
  onNewChat,
  onLoadConversation,
  activeThreadId,
  hasMessages,
  isScrolled,
}: OptiBotHeaderProps) {
  const { t } = useTranslation();
  const setOpen = useOptibotStore((s) => s.setOpen);
  // Device-troubleshoot session controls (ours): the Wrench button toggles the
  // session-history view and clears any focused session so the list shows.
  const showHistory = useOptibotStore((s) => s.showHistory);
  const setShowHistory = useOptibotStore((s) => s.setShowHistory);
  const closeSession = useOptibotStore((s) => s.closeSession);
  // Panel-layout state (theirs): drives the minimize-button glyph + view-mode menu.
  const viewMode = useOptibotStore((s) => s.viewMode);
  const isNarrow = useOptibotStore((s) => s.isNarrow);
  const checklistOpen = useOptibotStore((s) => s.checklistOpen);
  // While narrow OR while the Get Started checklist is stacked above, the panel
  // renders floating regardless of the stored mode.
  const effectiveViewMode = isNarrow || checklistOpen ? 'floating' : viewMode;
  // Account-wide gate: plan (secureLink) + kill switch OFF (disableSecureLink, inverted).
  // Per-device reachability is gated separately, on the device drawer's Troubleshoot tab.
  const { secureLinkTroubleshootEnabled } = useCompanyFeatures();

  // Selecting a chat conversation from recents — or starting a new chat — must EXIT any troubleshoot
  // view first (focused session / sessions-history), otherwise the panel keeps rendering the session/
  // history over the chat (focusedSession ? Pane : showHistory ? History : chat).
  const handleLoadConversation = (threadId: string) => {
    closeSession();
    setShowHistory(false);
    onLoadConversation?.(threadId);
  };
  const handleNewChat = onNewChat
    ? () => {
        closeSession();
        setShowHistory(false);
        onNewChat();
      }
    : undefined;

  return (
    // relative z-10 lifts the header above the scrolling messages so its bottom shadow
    // paints over them — only once scrolled down (no shadow when pinned at the top).
    <div
      className={cn('relative z-10 shrink-0 bg-card transition-shadow', isScrolled && 'shadow-sm')}
    >
      {/* Title row: OptiBot avatar + title on the left, action icons on the right. No
          divider underneath; the support callout below provides its own separation. */}
      <div className="flex items-center justify-between gap-2 px-3 py-3">
        {/* Top-left: OptiBot icon + title — always shown (including the empty chat) so
            the panel reads as OptiBot from the first frame. */}
        <div className="flex min-w-0 items-center gap-2">
          <BotAvatar />
          <h2 className="text-lg font-semibold text-foreground">{t('optibot.title')}</h2>
        </div>
        <div className="flex shrink-0 items-center gap-0">
          <HistoryDropdown
            onLoadConversation={handleLoadConversation}
            activeThreadId={activeThreadId}
            hasMessages={hasMessages}
          />
          {secureLinkTroubleshootEnabled && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={showHistory ? 'secondary' : 'ghost'}
                  size="icon-sm"
                  onClick={() => {
                    // Toggle the troubleshoot-session history view; clear any focused session so the list shows.
                    closeSession();
                    setShowHistory(!showHistory);
                  }}
                  aria-label={t('optibot.session.history.title')}
                  className="cursor-pointer"
                >
                  <Wrench className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('optibot.session.history.title')}</TooltipContent>
            </Tooltip>
          )}
          {handleNewChat && (
            <TipIconButton label={t('optibot.newChat')} onClick={handleNewChat}>
              <MessageCirclePlus className="h-4 w-4" />
            </TipIconButton>
          )}
          <ViewModeMenu />
          <TipIconButton label={t('optibot.minimizePanel')} onClick={() => setOpen(false)}>
            {/* Sidebar collapses to the right edge → double-chevron; the floating
                window just hides → minus. */}
            {effectiveViewMode === 'sidebar' ? (
              <ChevronsRight className="h-4 w-4" />
            ) : (
              <Minus className="h-4 w-4" />
            )}
          </TipIconButton>
        </div>
      </div>
    </div>
  );
});

// --- View-mode switcher (sidebar / floating) ---

function ViewModeMenu() {
  const { t } = useTranslation();
  const viewMode = useOptibotStore((s) => s.viewMode);
  const setViewMode = useOptibotStore((s) => s.setViewMode);
  const isNarrow = useOptibotStore((s) => s.isNarrow);
  const checklistOpen = useOptibotStore((s) => s.checklistOpen);
  // While narrow OR the checklist is stacked above, the panel is forced to
  // floating — docking is unavailable, so reflect floating as the active mode.
  const forcedFloating = isNarrow || checklistOpen;
  const activeMode = forcedFloating ? 'floating' : viewMode;
  const TriggerIcon = VIEW_ICONS[activeMode];

  return (
    <DropdownMenu>
      <Tooltip>
        <DropdownMenuTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('optibot.view.label')}
              className="cursor-pointer"
            >
              <TriggerIcon className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
        </DropdownMenuTrigger>
        <TooltipContent>{t('optibot.view.label')}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-52">
        {VIEW_OPTIONS.map(({ value, icon: Icon, labelKey }) => {
          const isActive = activeMode === value;
          // Docking is unavailable while the window is too narrow OR while the
          // Get Started checklist is stacked above the floating panel.
          const blocked = forcedFloating && value === 'sidebar';
          // Reason shown in the blocked tooltip — narrow takes priority since it
          // is the harder constraint (the checklist can be minimized; width can't).
          const blockedReason = isNarrow
            ? t('optibot.view.tooSmall')
            : t('optibot.view.checklistOpen');
          const item = (
            <DropdownMenuItem
              key={value}
              data-testid={`optibot-view-mode-${value}-menu-item`}
              onSelect={(e) => {
                if (blocked) {
                  e.preventDefault();
                  return;
                }
                setViewMode(value);
              }}
              className={cn(
                'gap-2 whitespace-nowrap pr-8',
                isActive && 'bg-accent font-medium',
                blocked ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
              )}
            >
              <Icon className="h-4 w-4" />
              {t(labelKey)}
              {isActive && <Check className="absolute right-2 h-4 w-4" />}
            </DropdownMenuItem>
          );

          return blocked ? (
            <Tooltip key={value}>
              <TooltipTrigger asChild>{item}</TooltipTrigger>
              <TooltipContent side="top">{blockedReason}</TooltipContent>
            </Tooltip>
          ) : (
            item
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// --- History Dropdown (Popover with lazy-loaded thread list) ---

function HistoryDropdown({
  onLoadConversation,
  activeThreadId,
  hasMessages,
}: {
  onLoadConversation?: (threadId: string) => void;
  activeThreadId?: string | null;
  hasMessages?: boolean;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState<ConversationThread[]>([]);

  // Fetch on mount — only show button if threads exist
  useEffect(() => {
    const controller = new AbortController();
    const fetchHistory = async () => {
      try {
        const apiUrl = getChatApiUrl() + '/history';
        const token = useAuthStore.getState().token;
        if (!token) return;
        const res = await fetch(apiUrl, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) {
            data.sort(
              (a: ConversationThread, b: ConversationThread) =>
                new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
            );
            setThreads(data);
          }
        }
      } catch (err) {
        console.warn('[OptiBot] Failed to fetch conversation history:', err);
      }
    };
    fetchHistory();
    return () => controller.abort();
  }, []);

  // No threads → no button
  if (threads.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={t('optibot.recentConversations')}
              className="cursor-pointer"
            >
              <History className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
        </PopoverTrigger>
        <TooltipContent>{t('optibot.recentConversations')}</TooltipContent>
      </Tooltip>
      <PopoverContent align="end" sideOffset={6} className="w-64 p-0">
        <div className="border-b border-border px-3 py-2">
          <p className="text-xs font-medium text-muted-foreground">
            {t('optibot.recentConversations')}
          </p>
        </div>
        <div className="thin-scrollbar max-h-60 space-y-0.5 overflow-y-auto p-1.5">
          {threads.map((thread, index) => {
            // Active if explicitly matches, or first thread when chat has messages but no explicit threadId
            const isActive = activeThreadId
              ? activeThreadId === thread.threadId
              : hasMessages && index === 0;
            return (
              <button
                key={thread.threadId}
                type="button"
                disabled={isActive}
                className={cn(
                  'flex w-full items-start gap-2 rounded-md px-2.5 py-2 text-left transition-colors',
                  // Neutral active/hover surface — avoids the low-contrast green-on-green.
                  isActive ? 'bg-accent cursor-default' : 'cursor-pointer hover:bg-accent/60',
                )}
                onClick={() => {
                  if (isActive) return;
                  onLoadConversation?.(thread.threadId);
                  setOpen(false);
                }}
              >
                <MessageSquare className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium text-foreground">{thread.subject}</p>
                  {thread.updatedAt && (
                    <p className="text-[10px] text-muted-foreground">
                      {`Last activity: ${formatWithPattern(thread.updatedAt, 'MM/DD/YY, h:mm A')}`}
                    </p>
                  )}
                </div>
                {/* Checkmark marks the conversation currently open in the panel. */}
                {isActive && <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
